// src/pages/Home/index.jsx
import {
  Container, Typography, Box, Card, CardContent, Button, Chip, Stack, Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Fragment } from 'preact/jsx-runtime';

// ======= Estilos base =======
const Hero = styled('header')(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(12, 0, 10),
  background:
    theme.palette.mode === 'dark'
      ? `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
      : `linear-gradient(180deg, ${theme.palette.grey[100]} 0%, #fff 100%)`,
}));

const Logo = styled('img')({
  width: 120,
  height: 'auto',
  marginBottom: 16,
  opacity: 0.95,
});

const Section = styled('section')(({ theme }) => ({
  padding: theme.spacing(8, 0),
}));

// ======= Cards de servicio (simétricas) =======
const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  transition: 'transform .18s ease, box-shadow .18s ease',
  '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[4] },
}));

const ServiceContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.25),
  flexGrow: 1,
}));

// ======= Helpers =======
const go = (path) => () => { window.location.href = path; };

import { useAppConfig } from '../../../../shared/components/utils/AppConfig';

export function Home() {
  const { appConfig } = useAppConfig();
  const logoUrl = appConfig?.branding?.logoUrl || "/public/img/adevslogo.png";
  const appTitle = appConfig?.app?.name || "AguayoDevs";
  const appSubtitle = appConfig?.branding?.subtitle || "Soluciones de software a la medida, enfocadas en calidad, seguridad y entrega continua.";

  return (
    <Box>
      {/* HERO */}
      <Hero>
        <Container maxWidth="md">
          {/* Mantengo tu ruta bajo /public */}
          <Logo src={logoUrl} alt={appTitle} />
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800, letterSpacing: '.2px' }}>
            {appTitle}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 880, mx: 'auto' }}>
            {appSubtitle}
          </Typography>
          <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 4 }}>
            <Button variant="contained" size="large" href="#contact">Cotiza tu Proyecto</Button>
            <Button variant="outlined" size="large" onClick={go('/adevs-client/login')}>Soy Cliente</Button>
          </Stack>
        </Container>
      </Hero>

      <Container maxWidth="lg">
        {/* SOBRE NOSOTROS */}
        <Section id="about">
          <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 800 }}>
            Sobre Nosotros
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ maxWidth: 920, mx: 'auto' }}>
            Equipo con foco en productos robustos y escalables. Buenas prácticas (tests, seguridad, observabilidad)
            y pipelines de entrega continua para acelerar el time-to-market sin sacrificar calidad.
          </Typography>
        </Section>

        {/* SERVICIOS (CSS Grid con Box) */}
        <Section>
          <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 800 }}>
            Nuestros Servicios
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            }}
          >
            {[
              {
                title: 'Desarrollo Web',
                desc: 'Frontends SSR/SSG y SPAs performantes con Preact/React, MUI y Vite; backends Node.js/Express integrados a tu core.',
              },
              {
                title: 'Aplicaciones Móviles',
                desc: 'Soluciones nativas o híbridas con foco en UX, rendimiento y analítica; publicación y CI para stores.',
              },
              {
                title: 'Consultoría & Arquitectura',
                desc: 'Definición de arquitectura, roadmap técnico, refactorizaciones y planes de escalabilidad.',
              },
              {
                title: 'Automatizaciones CI/CD',
                desc: 'Pipelines Azure DevOps/GitHub Actions: build, tests, quality gates y despliegues canary/blue-green.',
              },
              {
                title: 'Cloud & Contenedores',
                desc: 'Google Cloud, AWS y Azure; Docker, Nginx, Kubernetes, observabilidad y coste/escala controlados.',
              },
              {
                title: 'Seguridad & Mantenimiento',
                desc: 'Mitigación de vulnerabilidades (OWASP), actualización de dependencias, SAST/DAST, hardening y auditorías.',
              },
            ].map((s) => (
              <ServiceCard key={s.title}>
                <ServiceContent>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>
                    {s.title}
                  </Typography>
                  <Typography color="text.secondary">{s.desc}</Typography>
                  <Box sx={{ flexGrow: 1 }} />
                </ServiceContent>
              </ServiceCard>
            ))}
          </Box>
        </Section>

        {/* CAPACIDADES CLAVE */}
        <Section>
          <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 800 }}>
            Capacidades Clave
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              alignItems: 'stretch',
            }}
          >
            {[
              {
                title: 'Entrega Continua',
                body:
                  'CI/CD con stages de build, test (unit/integration/e2e), quality gates y despliegue automático con estrategias canary o blue-green.',
                chips: ['Azure DevOps', 'GitHub Actions', 'Nx/Vite', 'Playwright', 'Vitest/Jest'],
              },
              {
                title: 'Arquitectura en la Nube',
                body:
                  'Diseño e implementación en Google Cloud, Azure y AWS con balanceo, CDN, observabilidad y escalado horizontal/vertical.',
                chips: ['Google Cloud', 'AWS', 'Azure', 'Docker', 'Nginx', 'Kubernetes'],
              },
              {
                title: 'Seguridad & Calidad',
                body:
                  'Mitigación de vulnerabilidades, actualización de dependencias, SAST/DAST, protección de secretos y hardening de contenedores.',
                chips: ['OWASP Top 10', 'SAST/DAST', 'Dependabot/Renovate', 'CSP/Headers', 'Auditorías'],
              },
              {
                title: 'Integraciones & Datos',
                body:
                  'Pasarelas de pago, analítica y APIs de terceros; bases SQL/NoSQL y cachés para alto rendimiento.',
                chips: ['Stripe', 'MercadoPago', 'Facebook API/Pixel', 'OpenAI APIs', 'MySQL', 'PostgreSQL', 'SQL Server', 'MongoDB', 'Redis'],
              },
            ].map((b) => (
              <Card key={b.title} sx={{ borderRadius: 2, p: 2.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                  {b.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {b.body}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {b.chips.map((t) => <Chip key={t} label={t} size="small" />)}
                </Stack>
              </Card>
            ))}
          </Box>
        </Section>


        {/* TECNOLOGÍAS */}
        <Section>
          <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 800 }}>
            Tecnologías que dominamos
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Selección representativa; ajustamos el stack a tus objetivos y contexto.
          </Typography>

          <Card sx={{ borderRadius: 2 }}>
            <Box
              component="dl"
              sx={{
                m: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                columnGap: 3,
              }}
            >
              {[
                { k: 'Frontend', v: ['Preact/React', 'MUI', 'Vite', 'SSR/SSG', 'TypeScript'] },
                { k: 'Backend',  v: ['Node.js', 'Express', 'REST', 'WebSockets', 'JWT/OAuth'] },
                { k: 'DevOps',   v: ['Azure DevOps', 'GitHub Actions', 'Docker', 'Nginx', 'Kubernetes'] },
                { k: 'Cloud',    v: ['Google Cloud', 'AWS', 'Azure'] },
                { k: 'Datos',    v: ['MySQL', 'PostgreSQL', 'SQL Server', 'MongoDB', 'Redis'] },
                { k: 'Integraciones', v: ['Stripe', 'MercadoPago', 'Facebook API/Pixel', 'OpenAI APIs'] },
              ].map((row, i, arr) => (
                <Fragment key={row.k}>
                  <Box
                    component="dt"
                    sx={{
                      m: 0, px: 2.5, py: 2,
                      borderBottom: (t) => i < arr.length - 1 ? `1px solid ${t.palette.divider}` : 'none',
                      fontWeight: 800,
                    }}
                  >
                    {row.k}
                  </Box>
                  <Box
                    component="dd"
                    sx={{
                      m: 0, px: 2.5, py: 2,
                      borderBottom: (t) => i < arr.length - 1 ? `1px solid ${t.palette.divider}` : 'none',
                    }}
                  >
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {row.v.map((t) => <Chip key={t} label={t} size="small" />)}
                    </Stack>
                  </Box>
                </Fragment>
              ))}
            </Box>
          </Card>
        </Section>


        {/* CONTACTO / CTA FINAL */}
        <Section id="contact">
          <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 800 }}>
            Contacto
          </Typography>
          <Typography variant="body1" align="center">¿Tienes un proyecto en mente? ¡Hablemos!</Typography>
          <Typography variant="body1" align="center" sx={{ mt: 1 }}>
            <strong>Email:</strong> <a href="mailto:admin@aguayodevs.com">admin@aguayodevs.com</a>
          </Typography>
          <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 3 }}>
            <Button variant="contained" size="large" onClick={go('/adevs-client/login')}>Iniciar Sesión</Button>
            <Button variant="outlined" size="large" onClick={go('/adevs-client/register')}>Registrarse</Button>
          </Stack>
        </Section>
      </Container>
    </Box>
  );
}
